import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Radio, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-black text-xl tracking-tighter">Incident Tracking &amp; Inquiry Services</span>
        </div>

        {!token ? (
          <div data-testid="reset-no-token">
            <h2 className="font-heading text-3xl font-black tracking-tighter mb-3">Invalid link</h2>
            <p className="text-sm text-muted-foreground mb-6">
              This reset link is missing its token. Request a fresh one and use the newest email.
            </p>
            <Link to="/forgot-password" data-testid="reset-request-new" className="text-[#007AFF] hover:underline text-sm">
              Request a new reset link
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-4" />
            <h2 className="font-heading text-3xl font-black tracking-tighter mb-3">Password updated</h2>
            <p className="text-sm text-muted-foreground mb-6">Taking you to sign in…</p>
            <Link to="/login" data-testid="reset-go-login" className="text-[#007AFF] hover:underline text-sm">Sign in now</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Account Recovery</p>
            <h2 className="font-heading text-3xl font-black tracking-tighter mb-6">Choose a new password</h2>

            {error && (
              <div className="mb-4 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">New password</Label>
                <Input data-testid="reset-password-input" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="••••••••" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Confirm password</Label>
                <Input data-testid="reset-confirm-input" type="password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="••••••••" />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full mt-6 bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold h-11">
              <KeyRound className="w-4 h-4" /> {loading ? "Updating…" : "Set new password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
